import React, { useMemo } from "react";
import { Form, Input, Row, Col, FormGroup, Label } from "reactstrap";
import { useTranslation } from "react-i18next";
import Select from "components/Select";
import ReactDatetime from "components/CustomDatePicker";
import { formatDateForAPI, formatDateForPicker } from "utils/dateUtils";
import { DEAL_SEARCH_DYNAMIC_DATE_OPTIONS } from "utils/dynamicDateRange";
import { getActiveColorStyle } from "utils/colorUtils";
import {
  getTheme2MultiSelectSelectConfig,
  getTheme2SearchFilterColProps,
  getTheme2SearchFilterMultiSelectStyles,
  getTheme2SearchFilterSingleSelectStyles,
} from "utils/theme2MultiSelect";

const SEARCH_DEALS_STATUS_PREFIX = "search-deals-status";
const SEARCH_DEALS_WORKSHEET_TYPE_PREFIX = "search-deals-worksheet-type";

const SearchDealsFilters = ({
  activeColor = "blue",
  isTheme2 = false,
  search,
  onSearchChange,
  status,
  onStatusChange,
  worksheetTypes = [],
  onWorksheetTypesChange,
  statusOptions = [],
  worksheetTypeOptions = [],
  showWorksheetTypeFilter = true,
  showTenantFilter = false,
  onTenantChange,
  tenantValue = null,
  tenantOptions = [],
  isLoadingTenants = false,
  tenantLabel = "Tenant",
  dateFilterType = "dynamic",
  onDateFilterTypeChange,
  dynamicRange = "115",
  onDynamicRangeChange,
  beginningDate = "",
  onBeginningDateChange,
  endDate = "",
  onEndDateChange,
  showDateFilters = true,
}) => {
  const { t } = useTranslation();
  const activeColorStyle = useMemo(
    () => getActiveColorStyle(activeColor),
    [activeColor]
  );
  const statusMultiSelectStyles = useMemo(
    () => getTheme2SearchFilterMultiSelectStyles(activeColorStyle, isTheme2),
    [activeColorStyle, isTheme2]
  );
  const filterSelectStyles = useMemo(
    () => getTheme2SearchFilterSingleSelectStyles(activeColorStyle, isTheme2),
    [activeColorStyle, isTheme2]
  );
  const statusTheme2Config = useMemo(
    () => getTheme2MultiSelectSelectConfig(SEARCH_DEALS_STATUS_PREFIX, isTheme2),
    [isTheme2]
  );
  const worksheetTypeTheme2Config = useMemo(
    () => getTheme2MultiSelectSelectConfig(SEARCH_DEALS_WORKSHEET_TYPE_PREFIX, isTheme2),
    [isTheme2]
  );
  const worksheetTypeMultiSelectStyles = useMemo(
    () => getTheme2SearchFilterMultiSelectStyles(activeColorStyle, isTheme2),
    [activeColorStyle, isTheme2]
  );

  const dateFilterTypeOptions = useMemo(
    () => [
      { value: "dynamic", label: t("searchDeals.dynamicRange") },
      { value: "range", label: t("searchDeals.dateRange") },
    ],
    [t]
  );

  const baseFilterColProps = getTheme2SearchFilterColProps(isTheme2, {
    filterColClass: "search-deals-filter-col",
    md: "auto",
  });
  const filterColProps = isTheme2
    ? {
        ...baseFilterColProps,
        xs: "auto",
        sm: "auto",
        md: "auto",
        lg: "auto",
      }
    : baseFilterColProps;
  const searchFilterColProps = {
    ...filterColProps,
    className: `${filterColProps.className} search-deals-filter-col--search`,
  };
  const statusFilterColProps = {
    ...filterColProps,
    className: `${filterColProps.className} search-deals-filter-col--status`,
  };
  const worksheetTypeFilterColProps = {
    ...filterColProps,
    className: `${filterColProps.className} search-deals-filter-col--worksheet-type`,
  };
  const dateFilterColProps = {
    ...filterColProps,
    className: `${filterColProps.className} search-deals-filter-col--date`,
  };

  const dateInputProps = {
    className: "form-control",
  };

  return (
    <Form>
      <Row className={isTheme2 ? "search-deals-filters-row" : undefined}>
        <Col {...searchFilterColProps}>
          <FormGroup>
            <Label>{t("searchDeals.search")}:</Label>
            <Input
              type="text"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={t("searchDeals.searchPlaceholder")}
              className="form-control"
            />
          </FormGroup>
        </Col>
        <Col {...statusFilterColProps}>
          <FormGroup>
            <Label>{t("searchDeals.status")}:</Label>
            <Select
              activeColor={activeColor}
              className={statusTheme2Config.controlClassName}
              placeholder={t("searchDeals.selectStatus")}
              name="status"
              isSearchable={false}
              isMulti
              closeMenuOnSelect={false}
              hideSelectedOptions={statusTheme2Config.hideSelectedOptions}
              value={status}
              onChange={(option) => onStatusChange(option || [])}
              options={statusOptions}
              isClearable
              styles={statusMultiSelectStyles}
              components={statusTheme2Config.components}
              classNames={statusTheme2Config.classNames}
            />
          </FormGroup>
        </Col>
        {showTenantFilter && typeof onTenantChange === "function" ? (
          <Col {...filterColProps}>
            <FormGroup>
              <Label>{tenantLabel}:</Label>
              <Select
                activeColor={activeColor}
                className="react-select"
                styles={filterSelectStyles}
                placeholder={tenantLabel}
                name="tenant"
                isSearchable
                closeMenuOnSelect={true}
                value={tenantValue}
                onChange={(option) => onTenantChange(option?.value || "")}
                options={tenantOptions}
                isClearable
                isDisabled={isLoadingTenants}
              />
            </FormGroup>
          </Col>
        ) : null}
        {showWorksheetTypeFilter ? (
          <Col {...worksheetTypeFilterColProps}>
            <FormGroup>
              <Label>{t("searchDeals.worksheetType")}:</Label>
              <Select
                activeColor={activeColor}
                className={worksheetTypeTheme2Config.controlClassName}
                placeholder={t("searchDeals.selectWorksheetType")}
                name="worksheetType"
                isSearchable={false}
                isMulti
                closeMenuOnSelect={false}
                hideSelectedOptions={worksheetTypeTheme2Config.hideSelectedOptions}
                value={worksheetTypes}
                onChange={(option) => onWorksheetTypesChange?.(option || [])}
                options={worksheetTypeOptions}
                isClearable
                styles={worksheetTypeMultiSelectStyles}
                components={worksheetTypeTheme2Config.components}
                classNames={worksheetTypeTheme2Config.classNames}
              />
            </FormGroup>
          </Col>
        ) : null}
        {showDateFilters ? (
          <>
            <Col {...dateFilterColProps}>
              <FormGroup>
                <Label>{t("searchDeals.dateFilterType")}:</Label>
                <Select
                  activeColor={activeColor}
                  className="react-select"
                  styles={filterSelectStyles}
                  placeholder={t("searchDeals.dateFilterType")}
                  name="dateFilterType"
                  isSearchable={false}
                  closeMenuOnSelect={true}
                  value={
                    dateFilterTypeOptions.find((opt) => opt.value === dateFilterType) ||
                    dateFilterTypeOptions[0]
                  }
                  onChange={(option) =>
                    onDateFilterTypeChange?.(option?.value || "dynamic")
                  }
                  options={dateFilterTypeOptions}
                />
              </FormGroup>
            </Col>
            {dateFilterType === "dynamic" ? (
              <Col {...dateFilterColProps}>
                <FormGroup>
                  <Label>{t("searchDeals.dateRange")}:</Label>
                  <Select
                    activeColor={activeColor}
                    className="react-select"
                    styles={filterSelectStyles}
                    placeholder={t("searchDeals.selectDateRange")}
                    name="dynamicRange"
                    isSearchable
                    closeMenuOnSelect={true}
                    value={
                      DEAL_SEARCH_DYNAMIC_DATE_OPTIONS.find(
                        (opt) => opt.value === dynamicRange
                      ) ||
                      DEAL_SEARCH_DYNAMIC_DATE_OPTIONS.find(
                        (opt) => opt.value === "115"
                      ) ||
                      null
                    }
                    onChange={(option) =>
                      onDynamicRangeChange?.(option?.value || "115")
                    }
                    options={DEAL_SEARCH_DYNAMIC_DATE_OPTIONS}
                  />
                </FormGroup>
              </Col>
            ) : (
              <>
                <Col {...dateFilterColProps}>
                  <FormGroup>
                    <Label>{t("searchDeals.beginningDate")}:</Label>
                    <ReactDatetime
                      timeFormat={false}
                      dateFormat="MM-DD-YYYY"
                      value={formatDateForPicker(beginningDate) || ""}
                      onChange={(val) =>
                        onBeginningDateChange?.(formatDateForAPI(val) || "")
                      }
                      inputProps={{
                        ...dateInputProps,
                        placeholder: t("searchDeals.beginningDate"),
                      }}
                    />
                  </FormGroup>
                </Col>
                <Col {...dateFilterColProps}>
                  <FormGroup>
                    <Label>{t("searchDeals.endDate")}:</Label>
                    <ReactDatetime
                      timeFormat={false}
                      dateFormat="MM-DD-YYYY"
                      value={formatDateForPicker(endDate) || ""}
                      onChange={(val) =>
                        onEndDateChange?.(formatDateForAPI(val) || "")
                      }
                      inputProps={{
                        ...dateInputProps,
                        placeholder: t("searchDeals.endDate"),
                      }}
                    />
                  </FormGroup>
                </Col>
              </>
            )}
          </>
        ) : null}
      </Row>
    </Form>
  );
};

export default SearchDealsFilters;
